import { useState } from "react";
import styled from "styled-components";
import videojs from "video.js";
import { Box, Flex, Text } from "rebass/styled-components";
import { IoShareSocialOutline } from "react-icons/io5";
import { MdOutlineCreate, MdOutlineSaveAlt } from "react-icons/md";
import { IoLink } from "react-icons/io5";
import { BiMovie } from "react-icons/bi";
import { useRecoilState, useRecoilValue } from "recoil";
import { useTranslation } from "react-i18next";

import { ReactComponent as HistoryLogo } from "../../../../Assets/Nav/nav-history.svg";
import {
  ActiveOverlayPopUpState,
  KnownState,
  PlatformState,
  ShouldDisplayOverlayState,
} from "MVVM/State/iframe";
import {
  ActivePlaylistClipState,
  NextUpPlaylistState,
} from "MVVM/State/embeddablePlaylist";
import { useAnalyticsContext } from "MVVM/Hooks/useAnalyticsContext";
import { isFaceItPartner } from "MVVM/Utilities/Player";
import { IClip } from "MVVM/@types";
import { isPartner, Partner } from "MVVM/Views/EmbeddablePlaylist/Utilities";
import "./index.css";

export const ButtonMenu = ({
  player,
  clip,
}: {
  player: videojs.Player | null;
  clip: IClip;
}) => {
  const [activeOverlayPopUp, setActiveOverlayPopUp] = useRecoilState(
    ActiveOverlayPopUpState,
  );

  const platform = useRecoilValue(PlatformState);
  const isHLTV = isPartner(platform, Partner.HLTV);
  const isFaceIt = isFaceItPartner(platform || "");

  const shouldDisplayOverlay = useRecoilValue(ShouldDisplayOverlayState);
  const known = useRecoilValue(KnownState);
  const activePlaylistClip = useRecoilValue(ActivePlaylistClipState);
  const isNextUpPlaylist = useRecoilValue(NextUpPlaylistState);
  const isInPlaylist = !!activePlaylistClip;
  const domain = window.location.origin;
  const ajs_aid = window.rudderanalytics.getAnonymousId();
  const url = `${domain}/clip/${clip.internalId}?utm_source=${
    activePlaylistClip?.developer?.slug ||
    new URLSearchParams(window.location.search).get("platform") ||
    "unknown"
  }&ajs_aid=${ajs_aid}`;
  const { t } = useTranslation("translation", {
    keyPrefix: "embeddablePlayer.buttonMenu",
  });

  const [copied, setCopied] = useState(false);
  const { track } = useAnalyticsContext();

  if (!shouldDisplayOverlay) return null;

  const handleCopyClick = async () => {
    await navigator.clipboard.writeText(
      `${domain}/clip/${clip.internalId}?utm_source=${
        activePlaylistClip?.developer?.slug ||
        new URLSearchParams(window.location.search).get("platform") ||
        "unknown"
      }`,
    );
    setActiveOverlayPopUp(null);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <Flex justifyContent="space-between">
      <ButtonContainer className="button-container">
        <IconContainer className="icon-container">
          {isNextUpPlaylist && (
            <OptionWrapper
              onClick={() => {
                track("Button - Click", {
                  locationIdentifier: "nextUp_button",
                  location: "embeddable_player_overlay",
                });

                if (activeOverlayPopUp === "nextUp") {
                  setActiveOverlayPopUp(null);
                } else {
                  setActiveOverlayPopUp("nextUp");
                  player?.pause();
                }
              }}
            >
              <StyledButton
                className="styled-button"
                $active={activeOverlayPopUp === "nextUp"}
              >
                <BiMovie />
              </StyledButton>
              <TextWrapper $hasActivePopup={!!activeOverlayPopUp}>
                <ButtonText className="button-text">Playlist</ButtonText>
              </TextWrapper>
            </OptionWrapper>
          )}
          <OptionWrapper
            onClick={() => {
              track("Button - Click", {
                locationIdentifier: "save_button",
                location: "embeddable_player_overlay",
              });

              if (activeOverlayPopUp === "save") {
                setActiveOverlayPopUp(null);
              } else {
                if (isFaceIt) {
                  setActiveOverlayPopUp("save");
                } else {
                  window.open(`${url}&save=true`, "_blank");
                }
                player?.pause();
              }
            }}
          >
            <StyledButton
              className="styled-button"
              $active={activeOverlayPopUp === "save"}
            >
              <MdOutlineSaveAlt />
            </StyledButton>
            <TextWrapper $hasActivePopup={!!activeOverlayPopUp}>
              <ButtonText className="button-text">
                {t("save", { defaultValue: "Save" })}
              </ButtonText>
            </TextWrapper>
          </OptionWrapper>
          <ShareButtonWrapper
            className="share-button-wrapper"
            onClick={() => {
              track("Button - Click", {
                locationIdentifier: "share_button",
                location: "embeddable_player_overlay",
              });
              if (activeOverlayPopUp === "share") {
                setActiveOverlayPopUp(null);
              } else {
                setActiveOverlayPopUp("share");
                player?.pause();
              }
            }}
          >
            <StyledButton
              className="styled-button"
              $active={activeOverlayPopUp === "share"}
            >
              <IoShareSocialOutline
                style={{ marginTop: "2px", marginRight: "2px" }}
              />
            </StyledButton>
            <TextWrapper $hasActivePopup={!!activeOverlayPopUp}>
              <ButtonText className="button-text">
                {t("share", { defaultValue: "Share" })}
              </ButtonText>
            </TextWrapper>
          </ShareButtonWrapper>

          <OptionWrapper
            data-testid="copy-link-button"
            onClick={() => {
              track("Button - Click", {
                locationIdentifier: "copy_button",
                location: "embeddable_player_overlay",
              });
              handleCopyClick();
            }}
          >
            <StyledButton
              className="styled-button"
              $active={activeOverlayPopUp === "copy"}
            >
              <IoLink style={{ marginTop: "2px" }} />
            </StyledButton>
            <TextWrapper $hasActivePopup={!!activeOverlayPopUp}>
              <ButtonText
                className="button-text"
                style={{ color: copied ? "ice" : "envy" }}
              >
                {copied ? (
                  <CopiedText>
                    {t("copied", { defaultValue: "Copied!" })}
                  </CopiedText>
                ) : (
                  t("copyLink", { defaultValue: "Copy Link" })
                )}
              </ButtonText>
            </TextWrapper>
          </OptionWrapper>

          <OptionWrapper
            onClick={() => {
              track("Button - Click", {
                locationIdentifier: "match_history_button",
                location: "embeddable_player_overlay",
              });
              if (activeOverlayPopUp === "history") {
                setActiveOverlayPopUp(null);
              } else {
                setActiveOverlayPopUp("history");
                player?.pause();
              }
            }}
          >
            <StyledButton
              className="styled-button"
              $active={activeOverlayPopUp === "history"}
            >
              <HistoryLogo />
            </StyledButton>
            <TextWrapper $hasActivePopup={!!activeOverlayPopUp}>
              <ButtonText className="button-text">
                <Box as="span" className="small-screen-text">
                  {t("matches", { defaultValue: "Matches" })}
                </Box>
                <Box as="span" className="large-screen-text">
                  {t("matchHistory", { defaultValue: "Match History" })}
                </Box>
              </ButtonText>
            </TextWrapper>
          </OptionWrapper>
          {!isHLTV && (
            <EditButtonWrapper
              className="edit-button-wrapper"
              data-testid="edit-button"
              onClick={() => {
                track("Button - Click", {
                  locationIdentifier: "edit_button",
                  location: "embeddable_player_overlay",
                });
                if (known) {
                  const editParam = isInPlaylist ? "remix=true" : "edit=true";
                  return window.open(`${url}&${editParam}`, "_blank");
                }

                if (activeOverlayPopUp === "edit") {
                  setActiveOverlayPopUp(null);
                } else {
                  setActiveOverlayPopUp("edit");
                  player?.pause();
                }
              }}
            >
              <StyledButton
                className="styled-button"
                $active={activeOverlayPopUp === "edit"}
              >
                <MdOutlineCreate />
              </StyledButton>
              <TextWrapper $hasActivePopup={!!activeOverlayPopUp}>
                <ButtonText className="button-text">
                  {t("edit", { defaultValue: "Edit" })}
                </ButtonText>
              </TextWrapper>
            </EditButtonWrapper>
          )}
        </IconContainer>
      </ButtonContainer>
    </Flex>
  );
};

const OptionWrapper = styled(Flex)`
  cursor: pointer;
  pointer-events: auto;
  position: relative;

  &:hover {
    color: ${({ theme }) => theme.colors.envy};
    fill: ${({ theme }) => theme.colors.envy};
    * {
      color: ${({ theme }) => theme.colors.envy};
    }
    svg {
      fill: ${({ theme }) => theme.colors.envy};
    }
    .bell-icon {
      filter: brightness(0) saturate(100%) invert(74%) sepia(12%)
        saturate(1655%) hue-rotate(107deg) brightness(86%) contrast(88%);
    }
  }
`;

const ButtonContainer = styled(Flex)`
  align-items: flex-start;
  margin-left: 10px;
  pointer-events: auto;
`;

const IconContainer = styled(Flex)`
  background-color: rgba(0, 0, 0, 0.4);
  backdrop-filter: blur(2px);
  border-radius: 50px;
  flex-direction: column;
  gap: 8px;
  padding: 5px;
  width: fit-content;
`;

const StyledButton = styled(Flex)<{ $active: boolean }>`
  align-items: center;
  background-color: #000000bf;
  border-radius: 100px;
  cursor: pointer;
  justify-content: center;
  padding: 7px 8px 9px;
  border: ${({ theme, $active }) =>
    $active ? `1px solid ${theme.colors.envy}` : "1px solid #000000BF"};
  color: ${({ theme, $active }) =>
    $active ? theme.colors.envy : theme.colors.ice};

  svg {
    fill: ${({ theme, $active }) =>
      $active ? theme.colors.envy : theme.colors.ice};
    flex-shrink: 0;
  }

  &:hover {
    border: 1px solid ${({ theme }) => theme.colors.envy};
    color: ${({ theme }) => theme.colors.envy};

    svg {
      fill: ${({ theme }) => theme.colors.envy};
    }
  }
`;

const TextWrapper = styled(Flex)<{ $hasActivePopup: boolean }>`
  position: absolute;
  left: 165%;
  width: 100px;
  justify-content: flex-start;
  opacity: ${({ $hasActivePopup }) => ($hasActivePopup ? 0 : 1)};
  transition: opacity 0.2s ease-in-out;
`;

const ButtonText = styled(Text)`
  align-items: center;
  color: ${({ theme }) => theme.colors.ice};
  display: flex;
  justify-content: flex-start;
  letter-spacing: 0%;
  text-align: left;
  text-shadow: 0px 0px 8px rgba(0, 0, 0, 1);
  vertical-align: middle;

  .small-screen-text {
    display: inline;
    font-size: inherit !important;
  }

  .large-screen-text {
    display: none;
    font-size: inherit !important;
  }
`;

const CopiedText = styled(Text)`
  color: ${({ theme }) => theme.colors.envy};
`;

const ShareButtonWrapper = styled(OptionWrapper)``;

const EditButtonWrapper = styled(OptionWrapper)``;
